/**
 * The token from a copy-address confirmation link.
 *
 * The emailed link is `…/confirm-notification-address#token=…`, with the token
 * in the URL fragment so it never reaches a server log or a `Referer` header.
 * That is not enough on its own here: Sentry (tracing and session replay) and
 * LogRocket record the page URL, and they start before any component renders.
 * So the token is lifted out of the URL, into memory, and the fragment removed,
 * by `captureConfirmationToken` at the very start of bootstrap — before any of
 * them initialise.
 *
 * It only ever acts on the confirmation page's own path.
 */

/** Where the emailed link lands. Must match the backend's link. */
export const CONFIRM_ADDRESS_PATH = '/confirm-notification-address';

let captured: string | null = null;

export function captureConfirmationToken(): void {
  if (window.location.pathname !== CONFIRM_ADDRESS_PATH) {
    return;
  }
  const fragment = window.location.hash.replace(/^#/, '');
  const token = new URLSearchParams(fragment).get('token');
  if (token) {
    captured = token;
  }
  // Remove the fragment whether or not it held a token, so nothing downstream
  // sees it.
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.search
  );
}

/** The captured token, or null if the link carried none (or was opened again
 *  after a reload, which drops it). Reading does not clear it. */
export function getConfirmationToken(): string | null {
  return captured;
}

/** For tests. */
export function resetConfirmationToken(): void {
  captured = null;
}
